import { Injectable } from '@angular/core';
import * as moment from 'moment';

export interface ReactionToiWarningParams {
  row: any;
  selectedLatency: any;
  targetOpenWindow: any;
  toiLeadHours: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReactionToiWarningService {
  getInlineWarningMessage(): string {
    return (
      'Your time of interest (TOI) is less than the sensor’s minimum required reaction time for the selected tasking tier. ' +
      'Please change the tasking tier to improve the collection certainty.'
    );
  }

  getToiLeadHours(targetOpenWindow: any, now: Date = new Date()): number | null {
    if (!targetOpenWindow) return null;

    const startDate: any = new Date(moment(targetOpenWindow).utc().format());
    if (isNaN(startDate.getTime())) return null;

    const hours = (startDate.getTime() - now.getTime()) / 3600 / 1000;
    return isFinite(hours) ? hours : null;
  }

  /**
   * Popup-free check used for inline warning banners.
   */
  exceedsToi(params: ReactionToiWarningParams): boolean {
    const { row, selectedLatency } = params;
    const toiLeadHours = Math.max(0, params.toiLeadHours);

    if (!row || !selectedLatency) return false;

    // Only applicable to selected sensors.
    // Prefer `checked` when present because some code paths may uncheck rows without updating `sensorSelected`.
    if (row?.checked !== undefined) {
      if (row.checked !== true) return false;
    } else if (!row?.sensorSelected) {
      return false;
    }

    if (toiLeadHours == null || isNaN(toiLeadHours)) return false;

    const requiredReactionHours = this.parseReactionWindowToHours(selectedLatency?.taskingwindow);
    if (requiredReactionHours == null || !isFinite(requiredReactionHours) || requiredReactionHours <= 0) return false;

    return toiLeadHours < requiredReactionHours;
  }

  private parseReactionWindowToHours(taskingwindow: any): number | null {
    if (!taskingwindow) return null;
    if (typeof taskingwindow === 'number' && isFinite(taskingwindow)) {
      return taskingwindow;
    }

    const text = String(taskingwindow).trim().toLowerCase();
    if (!text || text === 'n/a' || text === 'na' || text === 'not offered') return null;

    // Known non-parseable / schedule-based values in sensor data
    if (
      text === 'best effort' ||
      /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/.test(text) ||
      text.includes('every ') ||
      text.includes('utc')
    ) {
      return null;
    }

    const getMultiplier = (unit: string): number => {
      if (unit.startsWith('week') || unit === 'wk' || unit === 'wks') return 7 * 24;
      if (unit.startsWith('day') || unit === 'd') return 24;
      if (unit.startsWith('hour') || unit.startsWith('hr') || unit === 'h') return 1;
      if (unit.startsWith('minute') || unit.startsWith('min')) return 1 / 60;
      return 1;
    };

    // Range formats like "1-2 days" or "< 1.5-2 days" -> use upper bound
    const rangeMatch = text.match(
      /(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(week|weeks|wk|wks|day|days|d|hour|hours|hr|hrs|h|minute|minutes|min|mins)/
    );
    if (rangeMatch) {
      const upper = Number(rangeMatch[2]);
      if (!isFinite(upper)) return null;
      const unit = rangeMatch[3];
      return upper * getMultiplier(unit);
    }

    // Standard format: take the numeric value and infer unit from the text
    const numberMatch = text.match(/(\d+(?:\.\d+)?)/);
    if (!numberMatch) return null;
    const value = Number(numberMatch[1]);
    if (!isFinite(value)) return null;

    if (/(week|weeks|\bwk\b|\bwks\b)/.test(text)) return value * 7 * 24;
    if (/(day|days|\bd\b)/.test(text)) return value * 24;
    if (/(hour|hours|hr|hrs|\bh\b)/.test(text)) return value;
    if (/(minute|minutes|min|mins)/.test(text)) return value / 60;

    // If unit is unknown, assume hours (best-effort).
    return value;
  }

}
